import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { subYears } from 'date-fns';
import {
  ComboBox,
  DatePickerInput,
  ElementsRefsInterface,
  Input,
  Label,
  RadioText,
  RecordInterface,
} from '@components/common/Forms';
import { EmployerTooltipComboBox } from '@components/features/combobox/EmployerTooltipComboBox';
import { RatingWidget } from '@components/common';
import { ArrowPathIcon } from '@heroicons/react/24/solid';

const MIN_DATE = subYears(new Date(), 65);
const NOW_DATE = new Date();

interface IProps {
  record: RecordInterface;
  elementsRefs: ElementsRefsInterface,
  locale: Locale,
}

/**
 * @description Used for manipulating inputs from experience form
 * @param record - form record used for inputs
 * @param elementsRefs - refs to input. Used to set predictedGroup programmatically
 * @param locale - the current app locale
 */
export const ExperienceForm = ({ record, elementsRefs, locale }: IProps) => {
  const { t } = useTranslation('common');

  const [currentlyWorkingHere, setCurrentlyWorkingHere] = useState<boolean>(false);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [starsSelected, setStarsSelected] = useState<number>(0);

  // on checkbox change
  const onChangeCurrentlyWorkingHere = () => {
    setCurrentlyWorkingHere((prevState) => !prevState);
    if (!currentlyWorkingHere) {
      setEndDate(null);
    }
  };

  return (
    <div className="mb-5 grid gap-x-4 md:grid-cols-2">
      {/* job title autocomplete. uses name and id in API call */}
      <Label as="div" description={t('onboarding.step3.position.title')} required>
        <ComboBox
          {...record('title')}
          required
          minLength={3}
          placeholder={t('onboarding.step3.position.placeholder')}
            // save predicted group id in separate input
          onSelectedItemChange={(item) => {
            elementsRefs.predictedGroup.current?.setElementValue(item ? item['id' as keyof typeof item] : '');
          }}
          options={{
            valueFromKey: 'name',
            apiList: {
              fetchUrl: '/job-title-autocomplete',
              queryParams: (query: string) => ({ term: query }),
            },
          }}
        />
      </Label>

      {/* hidden input for predicted group id. it is set from combobox */}
      <Input hidden type="text" {...record('predictedGroup')} />

      {/* company combobox */}

      <Label htmlFor="company" description={t('onboarding.step3.company.name.title')} required>
        <EmployerTooltipComboBox
          record={record}
          recordName="company"
          placeholder={t('onboarding.step3.company.name.placeholder')}
          locale={locale}
        />
      </Label>
      <div className="mt-4">
        {/* checkbox for currently working here. not send in API */}
        <Label htmlFor="currentlyWorkHere">
          <Input
            type="checkbox"
            checked={currentlyWorkingHere}
            onChange={onChangeCurrentlyWorkingHere}
            id="currentlyWorkHere"
            name="currentlyWorkHere"
          />
          <RadioText>{t('onboarding.step3.until.present')}</RadioText>
        </Label>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Label htmlFor="start" description={t('onboarding.step3.start.date')} required>
            <div>
              <DatePickerInput
                {...record('start')}
                required
                placeholder={t('global.yearmonth.placeholder')}
                date={startDate}
                setDate={(date) => setStartDate(date)}
                pickerType="month"
                pickerProps={{
                  minDate: MIN_DATE,
                  maxDate: endDate ?? NOW_DATE,
                }}
              />
            </div>
          </Label>

          {!currentlyWorkingHere && (
            <Label htmlFor="end" description={t('onboarding.step3.end.date')} required>
              <div>
                <DatePickerInput
                  {...record('end')}
                  required
                  placeholder={t('global.yearmonth.placeholder')}
                  date={endDate}
                  setDate={(date) => setEndDate(date)}
                  pickerType="month"
                  pickerProps={{
                    minDate: startDate ?? MIN_DATE,
                    maxDate: NOW_DATE,
                  }}
                />
              </div>
            </Label>
          )}
        </div>
      </div>

      <Label description={t('experience.rating.placeholder')}>
        <div className="mb-4 flex items-center gap-6 pt-3">
          <RatingWidget
            defaultValue={starsSelected}
            onChange={(newValue) => setStarsSelected(newValue)}
            emptyStarClassName="text-ink"
          />
          <ArrowPathIcon onClick={() => setStarsSelected(0)} className="size-6 cursor-pointer" />
        </div>
        <Input hidden type="number" {...record('rating', { convertValue: 'toNumber' })} value={starsSelected} />
      </Label>

    </div>
  );
};
